import { SectionCard } from './SectionCard'
import type { CustomMetricEntry, DashboardMode } from '../../../types/dashboard'

interface Props {
  entries: CustomMetricEntry[]
  mode: DashboardMode
}

export function CustomMetricsSection({ entries, mode }: Props) {
  if (entries.length === 0) return null

  if (mode === 'campaign') {
    return (
      <SectionCard title="Custom Metrics" id="section-custom-metrics">
        <div className="space-y-6">
          {entries.map((entry, i) => (
            <div key={i} className="border border-gray-200 dark:border-gray-700 rounded-lg p-4">
              <div className="flex gap-6 mb-3 pb-3 border-b border-gray-100 dark:border-gray-800">
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Id:</span> {entry.id}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Name:</span> {entry.name}
                </span>
                <span className="text-sm text-gray-500 dark:text-gray-400">
                  <span className="font-medium text-gray-700 dark:text-gray-300">Type:</span> {entry.type}
                </span>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
                {entry.metrics.map(m => (
                  <div key={m.name} className="flex flex-col">
                    <span className="text-xs text-gray-500 dark:text-gray-400 break-all">{m.name}</span>
                    <span className="text-sm font-semibold text-gray-800 dark:text-gray-200">{m.count.toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      </SectionCard>
    )
  }

  // Ad unit mode: matrix table (size × content_type with metric columns)
  const allMetricNames = Array.from(
    new Set(entries.flatMap(e => e.metrics.map(m => m.name)))
  )

  return (
    <SectionCard title="Custom Metrics" id="section-custom-metrics">
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200 dark:border-gray-700">
              <th className="text-left py-2 pr-4 text-gray-500 dark:text-gray-400 font-medium">Size</th>
              <th className="text-left py-2 pr-4 text-gray-500 dark:text-gray-400 font-medium">Content Type</th>
              {allMetricNames.map(name => (
                <th key={name} className="text-right py-2 pr-4 text-gray-500 dark:text-gray-400 font-medium whitespace-nowrap">{name}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {entries.map((entry, i) => {
              const metricMap: Record<string, number> = {}
              entry.metrics.forEach(m => { metricMap[m.name] = m.count })
              return (
                <tr key={i} className="border-b border-gray-100 dark:border-gray-800 hover:bg-gray-50 dark:hover:bg-gray-800/40">
                  <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">{entry.size}</td>
                  <td className="py-3 pr-4 text-gray-700 dark:text-gray-300">{entry.content_type}</td>
                  {allMetricNames.map(name => (
                    <td key={name} className="py-3 pr-4 text-right text-gray-800 dark:text-gray-200">
                      {(metricMap[name] ?? 0).toLocaleString()}
                    </td>
                  ))}
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>
    </SectionCard>
  )
}
